import type { IExtensionResponse, INumberResponse } from '@/openapi-clients-generated/talk';

/**
 * A number's usage is no longer carried by a dedicated field — `faxType` is gone from the contract.
 * It is now derived from `voiceType` plus whether a target is set:
 *
 * | State              | voiceType | target                    |
 * | ------------------ | --------- | ------------------------- |
 * | Unrouted voice     | talk      | null                      |
 * | Routed voice       | talk      | a supported target        |
 * | Digital fax        | fax       | null                      |
 * | Analog fax (ATA)   | fax       | extension → ATA extension |
 *
 * A FusionPBX tenant (`voiceBackend: 'fpbx'`) has plain `talk` numbers whose routing lives in
 * FusionPBX — that is a property of the tenant, not the number, so callers gate on the backend
 * (see `isModernTalkBackend`) rather than on anything here.
 *
 * Everything below reads that table; nothing should test `voiceType === 'fax'` on its own, since
 * that alone doesn't distinguish digital from analog — and only digital fax may use `/faxes`.
 */

/** A number expressed only as the fields the routing model needs — lets callers pass partials. */
type RoutableNumber = Pick<INumberResponse, 'voiceType' | 'targetType' | 'targetId'>;

/**
 * ZynoFax / internet fax: faxes are sent and received through the `/faxes` API. This is the ONLY
 * kind of number the digital fax screens may offer.
 */
export function isDigitalFaxNumber(number: RoutableNumber): boolean {
    return number.voiceType === 'fax' && number.targetType == null && number.targetId == null;
}

/**
 * A physical fax machine behind an ATA. Its calls travel through the ATA and the carrier, never
 * through `/faxes`, so these must be kept out of every digital fax picker.
 */
export function isAnalogFaxNumber(number: RoutableNumber): boolean {
    return number.voiceType === 'fax' && number.targetType === 'extension';
}

/** Either flavour of fax — i.e. not a voice line. */
export function isFaxNumber(number: RoutableNumber): boolean {
    return number.voiceType === 'fax';
}

/** A voice line — routable to any supported target, or left unrouted. */
export function isVoiceNumber(number: RoutableNumber): boolean {
    return number.voiceType === 'talk';
}

/**
 * Numbers with nowhere to send an inbound call.
 *
 * Deleted numbers are excluded: the delete clears the routing target server-side, so every one of
 * them would otherwise read as a fixable gap. A number whose linked extension has direct dial on is
 * routed by that link rather than by a target. Only meaningful on the zynotalk backend: a FusionPBX
 * tenant's routing lives in FusionPBX, so callers must not run this for one.
 */
export function findUnroutedNumbers<T extends RoutableNumber & Pick<INumberResponse, 'deletedAt' | 'linkedExtensionDirectDial'>>(numbers: T[]): T[] {
    return numbers.filter(n => !n.deletedAt && isVoiceNumber(n) && !n.targetType && !n.linkedExtensionDirectDial);
}

/**
 * An ATA extension fronts a physical fax machine. It is the other half of the analog-fax pairing
 * above: the only extension an analog fax number may target, and one a voice number never may.
 *
 * Lives here rather than beside the admin target helpers so it stays importable from dependency-free
 * modules — those helpers reach the DataCache, which drags in the whole app shell.
 */
export function isAtaExtension(extension: Pick<IExtensionResponse, 'planType'>): boolean {
    return extension.planType === 'ata';
}

/**
 * An extension that is meant to dial out but has no number to dial out with.
 *
 * Not a cosmetic gap: without an outgoing caller ID the extension cannot place an external call at
 * all. Keyed on `externalOutbound` and nothing else — a plan that can't dial out isn't missing a
 * caller ID, it is configured. Having a phone or a softphone doesn't imply the plan reaches the
 * outside world: an internal extension and a receive-only one both have endpoints and neither may
 * ever hold an outbound number, so a warning on either could never be cleared.
 */
export function isMissingOutboundCallerId(extension: Pick<IExtensionResponse, 'capabilities' | 'outboundNumberId'>): boolean {
    return !extension.outboundNumberId && !!extension.capabilities?.externalOutbound;
}

/**
 * The `voiceType`/`targetType` pair collapsed into one value for pickers and summaries. Kept
 * separate from the labels below so callers can switch on it without string matching.
 */
export type NumberUsage = 'voice' | 'digitalFax' | 'analogFax';

export function numberUsage(number: RoutableNumber): NumberUsage {
    if (isAnalogFaxNumber(number)) return 'analogFax';
    if (isDigitalFaxNumber(number)) return 'digitalFax';
    return 'voice';
}

export const NUMBER_USAGE_LABELS: Record<NumberUsage, string> = {
    voice: 'Voice',
    digitalFax: 'ZynoFax (Internet Fax)',
    analogFax: 'Analog Fax Machine via ATA'
};

/**
 * Clearing fax is a dedicated one-field update: the API also clears the target and direct-dial
 * state, so a target sent alongside it would be dropped. Route the number in a second update.
 */
export const CLEAR_FAX_UPDATE = { voiceType: 'talk' } as const;
